import dayjs from 'dayjs';

export interface TimeSeriesDataPoint {
    metric: number;
    timestamp: Date;
}

/**
 * Generates mock time series data optimized for chart visualization
 *
 * @param days Number of days to generate data for
 * @param pointsPerDay Number of data points per day (hourly by default)
 * @param minValue Minimum value for the metric
 * @param maxValue Maximum value for the metric
 * @param trend Type of trend to generate in the data
 * @returns Array of TimeSeriesDataPoint objects
 */
export function generateTimeSeriesData(
    days = 60,
    pointsPerDay = 24,
    minValue = 10,
    maxValue = 100,
    trend: 'random' | 'upward' | 'downward' | 'sine' = 'random',
): TimeSeriesDataPoint[] {
    // Performance optimization: For large datasets, reduce the number of points
    // to maintain performance while still showing a representative pattern
    const maxDataPoints = 10_000;
    let adjustedPointsPerDay = pointsPerDay;

    // If we would generate too many points, reduce the sampling rate
    if (days * pointsPerDay > maxDataPoints) {
        adjustedPointsPerDay = Math.max(1, Math.floor(maxDataPoints / days));
    }

    const data: TimeSeriesDataPoint[] = [];
    const now = dayjs();
    const range = maxValue - minValue;
    const hoursPerPoint = 24 / adjustedPointsPerDay;

    // Pre-allocate an array of the expected size to avoid resizing
    const expectedSize = days * adjustedPointsPerDay;
    data.length = expectedSize;

    // Pre-generate random values if using the 'random' trend
    // This avoids generating new random values for each point
    const randomValues = trend === 'random' ? Array.from({ length: expectedSize }, () => Math.random()) : [];

    let index = 0;

    // Generate data points from past to present
    for (let day = days - 1; day >= 0; day--) {
        const progress = 1 - day / days; // 0 to 1 from past to present
        const currentDate = now.subtract(day, 'day');

        // Generate fewer points per day for better performance
        for (let point = 0; point < adjustedPointsPerDay; point++) {
            const timestamp = currentDate.add(point * hoursPerPoint, 'hour').toDate();
            let metric: number;

            switch (trend) {
                case 'upward':
                    // Simplified upward trend calculation
                    metric = minValue + range * progress + (randomValues[index] || Math.random()) * range * 0.2;
                    break;
                case 'downward':
                    // Simplified downward trend calculation
                    metric = maxValue - range * progress + (randomValues[index] || Math.random()) * range * 0.2;
                    break;
                case 'sine':
                    // Simplified sine wave pattern
                    metric =
                        minValue +
                        range * 0.5 +
                        Math.sin(progress * Math.PI * 4) * range * 0.3 +
                        (randomValues[index] || Math.random()) * range * 0.1;
                    break;
                case 'random':
                default: {
                    // Optimized random generation with less correlation dependency
                    const previousValue =
                        index > 0 ? data[index - 1]?.metric || (minValue + maxValue) / 2 : (minValue + maxValue) / 2;

                    const randomFactor = randomValues[index] || Math.random();
                    const change = (randomFactor - 0.5) * range * 0.1;

                    metric = Math.max(minValue, Math.min(maxValue, previousValue + change));
                    break;
                }
            }

            data[index] = {
                metric: Number(metric.toFixed(1)), // Reduce precision for better performance
                timestamp,
            };

            index++;
        }
    }

    // Trim array to actual size if needed
    return data.slice(0, index);
}

/**
 * Generates mock time series data with weekly patterns using optimized approach
 *
 * @param days Number of days to generate data for
 * @param pointsPerDay Number of data points per day
 * @param weekdayRange Value range for weekdays [min, max]
 * @param weekendRange Value range for weekends [min, max]
 * @returns Array of TimeSeriesDataPoint objects
 */
export function generateWeekdayPatternData(
    days = 60,
    pointsPerDay = 24,
    weekdayRange = [50, 100],
    weekendRange = [20, 60],
): TimeSeriesDataPoint[] {
    // Performance optimization: For large datasets, reduce the number of points
    const maxDataPoints = 5000;
    let adjustedPointsPerDay = pointsPerDay;

    // If we would generate too many points, reduce the sampling rate
    if (days * pointsPerDay > maxDataPoints) {
        adjustedPointsPerDay = Math.max(1, Math.floor(maxDataPoints / days));
    }

    const data: TimeSeriesDataPoint[] = [];
    const now = dayjs();
    const hoursPerPoint = 24 / adjustedPointsPerDay;

    // Pre-allocate array to avoid resizing
    data.length = days * adjustedPointsPerDay;

    // Pre-generate random values for better performance
    const randomValues = Array.from({ length: days * adjustedPointsPerDay }, () => Math.random());

    let index = 0;

    // Time-of-day factors can be pre-calculated once
    const timeOfDayFactors = Array.from({ length: adjustedPointsPerDay }, (_, point) => {
        const hourOfDay = point * hoursPerPoint;
        if (hourOfDay >= 9 && hourOfDay <= 17) {
            return 1.0;
        } // Working hours
        if (hourOfDay >= 0 && hourOfDay <= 6) {
            return 0.6;
        } // Night
        return 0.8; // Other times
    });

    for (let day = days - 1; day >= 0; day--) {
        const currentDate = now.subtract(day, 'day');
        const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
        const [min, max] = isWeekend ? weekendRange : weekdayRange;
        const range = max! - min!;

        for (let point = 0; point < adjustedPointsPerDay; point++) {
            const timestamp = currentDate.add(point * hoursPerPoint, 'hour').toDate();
            const timeOfDayFactor = timeOfDayFactors[point];

            // Use pre-generated random values
            const metric = min! + range * randomValues[index]! * timeOfDayFactor!;

            data[index] = {
                metric: Number(metric.toFixed(1)),
                timestamp,
            };

            index++;
        }
    }

    // Return the actual data without empty slots
    return data.slice(0, index);
}

/**
 * Generates summarized data points for larger time ranges to improve performance
 * For when you need overview data rather than every single point
 *
 * @param days Number of days to summarize data for
 * @param minValue Minimum value for the metric
 * @param maxValue Maximum value for the metric
 * @param trend Type of trend pattern
 * @returns One data point per day with min, max, and average values
 */
export function generateSummarizedTimeSeriesData(
    days = 365,
    minValue = 10,
    maxValue = 100,
    trend: 'random' | 'upward' | 'downward' | 'sine' = 'random',
): { average: number; max: number; min: number; timestamp: Date }[] {
    const now = dayjs();
    const range = maxValue - minValue;
    const result = [];

    // Generate just one summary point per day instead of multiple points
    for (let day = days - 1; day >= 0; day--) {
        const timestamp = now.subtract(day, 'day').startOf('day').toDate();
        const progress = 1 - day / days;

        let baseValue: number;
        switch (trend) {
            case 'upward':
                baseValue = minValue + range * progress;
                break;
            case 'downward':
                baseValue = maxValue - range * progress;
                break;
            case 'sine':
                baseValue = minValue + range * 0.5 + Math.sin(progress * Math.PI * 4) * range * 0.3;
                break;
            case 'random':
            default:
                baseValue = minValue + Math.random() * range;
                break;
        }

        // Generate a daily summary with min, avg, max
        const variance = range * 0.15; // Daily variance
        const min = Math.max(minValue, baseValue - Math.random() * variance);
        const max = Math.min(maxValue, baseValue + Math.random() * variance);
        const average = (min + max) / 2;

        result.push({
            average: Number(average.toFixed(1)),
            max: Number(max.toFixed(1)),
            min: Number(min.toFixed(1)),
            timestamp,
        });
    }

    return result;
}
